import { useRef, useState } from "react";
import "./ReportProblemModal.css";
import { useModalA11y } from "./useModalA11y";
import { API_URL, TECH_SUPPORT_EMAIL } from "../config";
import { track } from "../analytics/posthog";
import { useI18n } from "../i18n";

/**
 * "Something not working?" — the report form behind the footer link.
 *
 * A modal rather than a page (Naveen, 14 Sept): a resident mid-referral must
 * not lose their conversation to report that a button did not work. Replaces
 * the earlier mailto, which opened nothing on a phone with no mail client set
 * up — a dead link is worse than no link.
 *
 * Only the message is required. Asking for an email address before we will
 * take the report would lose exactly the reports worth having ("the page is
 * blank"), so the address is optional and used only to reply.
 *
 * Not a clinical route: the copy stays about the website, and Get Urgent Help
 * remains on every screen behind this overlay.
 */
type Status = "idle" | "sending" | "sent" | "failed";

export function ReportProblemModal({
  onClose,
  sessionId,
}: {
  onClose: () => void;
  sessionId?: string | null;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(dialogRef, onClose);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  // Name, email and message are all required (Naveen, 14 Sept), so the send
  // button stays disabled until each has something in it.
  const complete = Boolean(name.trim() && email.trim() && message.trim());

  const submit = async () => {
    const text = message.trim();
    if (!complete || status === "sending") return;
    setStatus("sending");
    try {
      const response = await fetch(`${API_URL}/v1/support/report`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          message: text,
          name: name.trim(),
          email: email.trim(),
          session_id: sessionId ?? null,
          // Context the resident cannot reliably give us themselves. Not
          // identifying: the browser string and the current path, no more.
          user_agent: navigator.userAgent.slice(0, 400),
          page: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      setStatus("sent");
      track("problem_reported");
    } catch {
      // Never a dead end: the failure state shows the address so the resident
      // can still reach us by their own mail client.
      setStatus("failed");
    }
  };

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        className="modal report-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-modal-title"
      >
        <h2 className="modal__title" id="report-modal-title">
          {t("report.title")}
        </h2>

        {status === "sent" ? (
          /* Success replaces the form rather than sitting above it — leaving a
             filled-in form on screen invites a second submission. */
          <>
            <p className="modal__intro" role="status">
              {t("report.thanks")}
            </p>
            <div className="report-actions">
              <button type="button" className="report-btn report-btn--primary" onClick={onClose}>
                {t("report.close")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="modal__intro">{t("report.intro")}</p>

            <label className="report-label" htmlFor="report-name">
              {t("report.nameLabel")}
            </label>
            <input
              id="report-name"
              className="report-input"
              type="text"
              autoComplete="name"
              maxLength={120}
              value={name}
              disabled={status === "sending"}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("report.namePlaceholder")}
            />

            <label className="report-label" htmlFor="report-email">
              {t("report.emailLabel")}
            </label>
            <input
              id="report-email"
              className="report-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              maxLength={254}
              value={email}
              disabled={status === "sending"}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("report.emailPlaceholder")}
            />

            <label className="report-label" htmlFor="report-message">
              {t("report.messageLabel")}
            </label>
            <textarea
              id="report-message"
              className="report-textarea"
              rows={5}
              maxLength={2000}
              value={message}
              disabled={status === "sending"}
              onChange={(e) => setMessage(e.target.value)}
            />

            {status === "failed" && (
              <p className="report-error" role="alert">
                {t("report.failed")}{" "}
                <a href={`mailto:${TECH_SUPPORT_EMAIL}`}>{TECH_SUPPORT_EMAIL}</a>
              </p>
            )}

            <div className="report-actions">
              <button
                type="button"
                className="report-btn report-btn--ghost"
                onClick={onClose}
                disabled={status === "sending"}
              >
                {t("report.cancel")}
              </button>
              <button
                type="button"
                className="report-btn report-btn--primary"
                onClick={submit}
                disabled={!complete || status === "sending"}
              >
                {status === "sending" ? t("report.sending") : t("report.send")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
